import { cn } from "@/utils/cn";
import {
  HTMLProps,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePickerActions, usePickerData } from "./picker";

interface PickerColumnProps extends HTMLProps<HTMLDivElement> {
  name: string;
}

const PickerColumnDataContext = createContext<{
  key: string;
} | null>(null);
PickerColumnDataContext.displayName = "PickerColumnDataContext";

export function useColumnData(componentName: string) {
  const context = useContext(PickerColumnDataContext);
  if (context === null) {
    const error = new Error(
      `<${componentName} /> is missing a parent <Picker.Column /> component.`,
    );
    if (Error.captureStackTrace) {
      Error.captureStackTrace(error, useColumnData);
    }
    throw error;
  }
  return context;
}

const PickerColumn = memo(function PickerColumn({
  style,
  children,
  name: key,
  ...restProps
}: PickerColumnProps) {
  const {
    height,
    itemHeight,
    wheelMode,
    enableVelocity,
    value: groupValue,
    optionGroups,
  } = usePickerData("Picker.Column");

  // Caculate the selected index
  const value = useMemo(() => groupValue[key], [groupValue, key]);
  const options = useMemo(() => optionGroups[key] || [], [key, optionGroups]);
  const selectedIndex = useMemo(() => {
    let index = options.findIndex((o) => o.value === value);
    if (index < 0) {
      index = 0;
    }
    return index;
  }, [options, value]);

  // Caculate the translate of scroller
  const minTranslate = useMemo(
    () => height / 2 - itemHeight * options.length + itemHeight / 2,
    [height, itemHeight, options],
  );
  const maxTranslate = useMemo(() => height / 2 - itemHeight / 2, [height, itemHeight]);
  const [scrollerTranslate, setScrollerTranslate] = useState<number>(0);
  useEffect(() => {
    setScrollerTranslate(height / 2 - itemHeight / 2 - selectedIndex * itemHeight);
  }, [height, itemHeight, selectedIndex]);

  // A handler to trigger the value change
  const pickerActions = usePickerActions("Picker.Column");
  const translateRef = useRef<number>(scrollerTranslate);
  translateRef.current = scrollerTranslate;
  const handleScrollerTranslateSettled = useCallback(() => {
    let nextActiveIndex = 0;
    const currentTrans = translateRef.current;
    if (currentTrans >= maxTranslate) {
      nextActiveIndex = 0;
    } else if (currentTrans <= minTranslate) {
      nextActiveIndex = options.length - 1;
    } else {
      nextActiveIndex = -Math.round((currentTrans - maxTranslate) / itemHeight);
    }

    const changed = pickerActions.change(key, options[nextActiveIndex].value);
    if (!changed) {
      setScrollerTranslate(height / 2 - itemHeight / 2 - nextActiveIndex * itemHeight);
    }
  }, [pickerActions, height, itemHeight, key, maxTranslate, minTranslate, options]);

  // Handle touch events
  const [startScrollerTranslate, setStartScrollerTranslate] = useState<number>(0);
  const [isMoving, setIsMoving] = useState<boolean>(false);
  const [startTouchY, setStartTouchY] = useState<number>(0);
  const touchHistoryRef = useRef<Array<{ y: number; ts: number }>>([]);
  const settleTimerRef = useRef<number | null>(null);

  const updateScrollerWhileMoving = useCallback(
    (nextScrollerTranslate: number) => {
      if (nextScrollerTranslate < minTranslate) {
        nextScrollerTranslate = minTranslate - Math.pow(minTranslate - nextScrollerTranslate, 0.8);
      } else if (nextScrollerTranslate > maxTranslate) {
        nextScrollerTranslate = maxTranslate + Math.pow(nextScrollerTranslate - maxTranslate, 0.8);
      }
      setScrollerTranslate(nextScrollerTranslate);
    },
    [maxTranslate, minTranslate],
  );

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      event.stopPropagation();
      const touchY = event.targetTouches[0].pageY;
      const now = Date.now();
      setStartTouchY(touchY);
      setStartScrollerTranslate(scrollerTranslate);
      touchHistoryRef.current = [{ y: touchY, ts: now }];
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
    },
    [scrollerTranslate],
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      event.stopPropagation();
      if (event.cancelable) {
        event.preventDefault();
      }

      if (!isMoving) {
        setIsMoving(true);
      }

      const nextScrollerTranslate =
        startScrollerTranslate + event.targetTouches[0].pageY - startTouchY;
      updateScrollerWhileMoving(nextScrollerTranslate);

      const now = Date.now();
      const touchY = event.targetTouches[0].pageY;
      const recentTouches = touchHistoryRef.current.filter((point) => now - point.ts <= 120);
      recentTouches.push({ y: touchY, ts: now });
      touchHistoryRef.current = recentTouches;
    },
    [isMoving, startScrollerTranslate, startTouchY, updateScrollerWhileMoving],
  );

  const handleTouchEnd = useCallback(() => {
    if (!isMoving) {
      return;
    }
    setIsMoving(false);
    setStartTouchY(0);
    setStartScrollerTranslate(0);
    if (!enableVelocity) {
      touchHistoryRef.current = [];
      handleScrollerTranslateSettled();
      return;
    }

    const points = touchHistoryRef.current;
    touchHistoryRef.current = [];
    if (points.length < 2) {
      handleScrollerTranslateSettled();
      return;
    }

    const lastPoint = points[points.length - 1];
    const firstPoint = points[0];
    if (!lastPoint || !firstPoint) {
      handleScrollerTranslateSettled();
      return;
    }

    const timeDelta = Math.max(lastPoint.ts - firstPoint.ts, 1);
    const velocity = (lastPoint.y - firstPoint.y) / timeDelta; // px/ms
    const velocityThreshold = 0.2;

    if (Math.abs(velocity) < velocityThreshold) {
      handleScrollerTranslateSettled();
      return;
    }

    const momentumDistance = velocity * 220;
    const targetTranslate = Math.max(
      minTranslate,
      Math.min(maxTranslate, translateRef.current + momentumDistance),
    );

    if (targetTranslate === translateRef.current) {
      handleScrollerTranslateSettled();
      return;
    }

    setScrollerTranslate(targetTranslate);
    settleTimerRef.current = setTimeout(() => {
      handleScrollerTranslateSettled();
      settleTimerRef.current = null;
    }, 320) as unknown as number;
  }, [enableVelocity, handleScrollerTranslateSettled, isMoving, maxTranslate, minTranslate]);

  const handleTouchCancel = useCallback(() => {
    if (!isMoving) {
      return;
    }
    setIsMoving(false);
    setStartTouchY(0);
    setScrollerTranslate(startScrollerTranslate);
    setStartScrollerTranslate(0);
    touchHistoryRef.current = [];
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, [isMoving, startScrollerTranslate]);

  // Handle wheel events
  const wheelingTimer = useRef<number | null>(null);

  const handleWheeling = useCallback(
    (event: WheelEvent) => {
      if (event.deltaY === 0) {
        return;
      }
      let delta = event.deltaY * 0.1;
      if (Math.abs(delta) < itemHeight) {
        delta = itemHeight * Math.sign(delta);
      }
      if (wheelMode === "normal") {
        delta = -delta;
      }

      const nextScrollerTranslate = scrollerTranslate + delta;
      updateScrollerWhileMoving(nextScrollerTranslate);
    },
    [itemHeight, scrollerTranslate, updateScrollerWhileMoving, wheelMode],
  );

  const handleWheelEnd = useCallback(() => {
    handleScrollerTranslateSettled();
  }, [handleScrollerTranslateSettled]);

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (wheelMode === "off") {
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      handleWheeling(event);

      if (wheelingTimer.current) {
        clearTimeout(wheelingTimer.current);
      }

      wheelingTimer.current = setTimeout(() => {
        handleWheelEnd();
      }, 200) as unknown as number;
    },
    [handleWheelEnd, handleWheeling, wheelingTimer, wheelMode],
  );

  // All touch/wheel listeners are native DOM listeners. React synthetic events
  // silently fail inside IonNav/IonModal subtrees (event delegation breaks), so
  // onTouchStart never fires → startTouchY/startScrollerTranslate stay at 0 →
  // picker scroll calculation is wrong. Native listeners on the element are
  // unaffected by this and work correctly everywhere.
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("touchstart", handleTouchStart, { passive: false });
      container.addEventListener("touchmove", handleTouchMove, { passive: false });
      container.addEventListener("touchend", handleTouchEnd);
      container.addEventListener("touchcancel", handleTouchCancel);
      container.addEventListener("wheel", handleWheel, { passive: false });
    }
    return () => {
      if (container) {
        container.removeEventListener("touchstart", handleTouchStart);
        container.removeEventListener("touchmove", handleTouchMove);
        container.removeEventListener("touchend", handleTouchEnd);
        container.removeEventListener("touchcancel", handleTouchCancel);
        container.removeEventListener("wheel", handleWheel);
      }
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel, handleWheel]);

  useEffect(() => {
    return () => {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
      }
    };
  }, []);

  const columnData = useMemo(() => ({ key }), [key]);

  return (
    <div
      className={cn("max-h-full flex-1 transition-transform ease-[cubic-bezier(0,0,0.2,1)]", {
        "duration-0": isMoving,
        "duration-300": !isMoving,
      })}
      style={{
        transform: `translate3d(0, ${scrollerTranslate}px, 0)`,
        ...style,
      }}
      ref={containerRef}
      {...restProps}
    >
      <PickerColumnDataContext.Provider value={columnData}>
        {children}
      </PickerColumnDataContext.Provider>
    </div>
  );
});

export default PickerColumn;
